import {
  AfterViewInit,
  Component,
  computed,
  inject,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
  output,
  OutputEmitterRef,
  Signal,
} from '@angular/core';
import {
  ICorporateDesign,
  INormalText,
  ERelationType,
  IDirectTicket,
  EEntityType,
  IFellowTravelerDto,
} from '@web/dtos';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ITicketOwnerDataForm } from '../../../pages/shop/direct-ticket-configuration-page/direct-ticket-owner-data-page/direct-ticket-owner-data-page.component';
import { ToTranslatedOptionPipe } from '../../../pipes/to-select-option/to-translated-option.pipe';
import { SalutationStore } from '../../../services/api/salutation/salutation-store.service';
import { LanguagePipe } from '../../../pipes/language/language.pipe';
import { FilterTextsPipe } from '../../../pipes/filterTexts/filterTexts.pipe';
import { AppendTestIdPipe } from '../../../pipes/append-test-id/append-test-id.pipe';
import { ShoppingCartService } from '../../../services/shopping-cart/shopping-cart.service';
import { Subscription } from 'rxjs';
import { SectionComponent } from '../../layout/section/section.component';
import { SectionHeaderComponent } from '../../layout/section/section-header/section-header.component';
import { CheckboxComponent } from '../form-inputs/checkbox/checkbox.component';
import { TextInputComponent } from '../form-inputs/text-input/text-input.component';
import { DropDownComponent } from '../form-inputs/drop-down/drop-down.component';
import { DateInputComponent } from '../form-inputs/date-input/date-input/date-input.component';
import { CorporateDesignService } from '../../../services/corporate-design/corporate-design.service';
import { WebTextStore } from '../../../services/api/text/web-text-store.service';
import { DirectTicketCompanionDataFormComponent } from '../direct-ticket-companion-data-form/direct-ticket-companion-data-form.component';
import { FellowTravelerStore } from '../../../services/api/fellow-traveler/fellow-traveler-store.service';
import { AuthProvider } from '../../../services/auth/auth.provider';
import { AuthProviderFactory } from '../../../services/auth/auth-provider.factory';

@Component({
  selector: 'aw-direct-ticket-owner-data-form',
  imports: [
    SectionComponent,
    SectionHeaderComponent,
    ReactiveFormsModule,
    CheckboxComponent,
    TextInputComponent,
    DropDownComponent,
    ToTranslatedOptionPipe,
    LanguagePipe,
    FilterTextsPipe,
    AppendTestIdPipe,
    DateInputComponent,
    DirectTicketCompanionDataFormComponent,
  ],
  templateUrl: './direct-ticket-owner-data-form.component.html',
})
export class DirectTicketOwnerDataFormComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  public readonly ticketOwnerForm: InputSignal<
    FormGroup<ITicketOwnerDataForm>
  > = input.required();
  public readonly ticketName: InputSignal<string> = input.required();
  public readonly id: InputSignal<string> = input.required();

  public readonly onChange: OutputEmitterRef<boolean> = output<boolean>();

  public readonly corporateDesignService: CorporateDesignService = inject(
    CorporateDesignService
  );
  private readonly salutationsStore: SalutationStore = inject(SalutationStore);
  private readonly shoppingCartService: ShoppingCartService =
    inject(ShoppingCartService);
  protected readonly textStore: WebTextStore = inject(WebTextStore);
  protected readonly fellowTravelerStore: FellowTravelerStore =
    inject(FellowTravelerStore);
  protected readonly auth: AuthProvider =
    inject(AuthProviderFactory).getProvider('shop');

  public readonly fellowTravelers: Signal<IFellowTravelerDto[]> =
    this.fellowTravelerStore.fellowTravelers;
  protected readonly corporateDesign: Signal<ICorporateDesign> =
    this.corporateDesignService.corporateDesign;
  protected readonly salutationOptions: Signal<INormalText[]> =
    this.salutationsStore.salutationOptions;
  protected readonly ticket: Signal<IDirectTicket | undefined> = computed(
    (): IDirectTicket | undefined => {
      return this.shoppingCartService
        .tickets()
        .find((ticket: IDirectTicket) => ticket.name === this.ticketName());
    }
  );
  protected readonly fellowTravelerOptions: Signal<
    { label: string; value: string }[]
  > = computed(() =>
    this.fellowTravelers()
      .filter(
        (
          fellowTraveler: IFellowTravelerDto
        ): fellowTraveler is IFellowTravelerDto & { id: string } =>
          Boolean(fellowTraveler.id)
      )
      .map(
        (
          fellowTraveler: IFellowTravelerDto & { id: string }
        ): { label: string; value: string } => ({
          label: `${fellowTraveler.firstName} ${fellowTraveler.lastName}`,
          value: fellowTraveler.id,
        })
      )
  );

  protected readonly isAlternativeTicketOwnerActive: Signal<boolean> =
    this.fellowTravelerStore.isActive;
  private readonly subscription: Subscription = new Subscription();
  protected readonly RelationType: typeof ERelationType = ERelationType;

  ngOnInit() {
    this.subscription.add(
      this.ticketOwnerForm().valueChanges.subscribe(() => {
        this.onChange.emit(this.ticketOwnerForm().valid);
      })
    );
    this.setAlternativeControlState(
      this.ticketOwnerForm().controls.isForUser.value
    );
    this.subscription.add(
      this.ticketOwnerForm().controls.fellowTravelerId.valueChanges.subscribe(
        (fellowTravelerId: string | undefined) => {
          const fellowTraveler = this.fellowTravelers().find(
            traveler => traveler.id === fellowTravelerId
          );

          const form = this.ticketOwnerForm().controls;
          if (fellowTraveler) {
            form.salutation.setValue(fellowTraveler.salutation ?? '');
            form.firstName.setValue(fellowTraveler.firstName);
            form.lastName.setValue(fellowTraveler.lastName);
            form.dateOfBirth.setValue(
              fellowTraveler.dateOfBirth
                ? new Date(`${fellowTraveler.dateOfBirth}T00:00:00`)
                : undefined
            );
          } else {
            form.salutation.setValue('');
            form.firstName.setValue('');
            form.lastName.setValue('');
            form.dateOfBirth.setValue(undefined);
            [
              form.salutation,
              form.firstName,
              form.lastName,
              form.dateOfBirth,
            ].forEach(control => {
              control.markAsPristine();
              control.markAsUntouched();
            });
          }
        }
      )
    );
  }

  ngAfterViewInit() {
    this.ticketOwnerForm().updateValueAndValidity();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  protected setAlternativeControlState(isForMe: boolean): void {
    const form: FormGroup<ITicketOwnerDataForm> = this.ticketOwnerForm();
    const personalData =
      this.shoppingCartService.personalDataForm.getRawValue();
    form.controls.fellowTravelerId.setValue(undefined);
    if (isForMe) {
      form.controls.salutation.setValue(personalData.salutation ?? '');
      form.controls.firstName.setValue(personalData.firstName ?? '');
      form.controls.lastName.setValue(personalData.lastName ?? '');
      form.controls.dateOfBirth.setValue(personalData.dateOfBirth ?? undefined);
      form.controls.salutation.disable();
      form.controls.firstName.disable();
      form.controls.lastName.disable();
      form.controls.dateOfBirth.disable();
    } else {
      form.controls.salutation.setValue('');
      form.controls.firstName.setValue('');
      form.controls.lastName.setValue('');
      form.controls.dateOfBirth.setValue(undefined);
      form.controls.salutation.enable();
      form.controls.firstName.enable();
      form.controls.lastName.enable();
      form.controls.dateOfBirth.enable();
    }
  }

  protected readonly ERelationType: typeof ERelationType = ERelationType;
  protected readonly EEntityType: typeof EEntityType = EEntityType;
}
